import { useEffect } from "react";
import { GetServerSideProps } from "next";
import { useDetailPageActivityMutation } from "src/api/client/detail-page-activity.api";
import { fetchWikifolioDetail } from "src/api/server/wf-detail";
import { IWikifolioDetailProps } from "src/api/server/wf-detail.types";
import { WikifolioDetailPage } from "src/components/pages/detail/wf-detail-page";
import { WikifolioDetailBreadcrumbSchema } from "src/components-mui/common/schemas/wikifolio-detail-breadcrumb-schema";
import { WikifolioDetailProductSchema } from "src/components-mui/common/schemas/wikifolio-detail-product-schema";
import { serverSideTranslations } from "src/lib/i18n/server-side-translations";
import { useUserStore } from "src/stores/user-store";
import { IBaseParam } from "src/types/common";
import { addServerTiming } from "src/utils/nextjs/add-server-timing";
import { setReturnUrl } from "src/utils/set-return-url";
import { normalizeWikifolioSymbol } from "src/utils/wikifolio-symbol";

interface IPageEffectsHandlerProps {
    wikifolioId: string;
}

function PageEffectsHandler({ wikifolioId }: IPageEffectsHandlerProps) {
    const trackVisitDetailPageActivity = useDetailPageActivityMutation();
    setReturnUrl();

    useEffect(() => {
        trackVisitDetailPageActivity({
            parameter: `wikifolioId=${wikifolioId}`,
            referrer: document?.referrer ? document.referrer : null,
        });
    }, [trackVisitDetailPageActivity, wikifolioId]);

    return null;
}

function Page({ data, dict, head }: IWikifolioDetailProps) {
    const { isLoggedIn } = useUserStore();
    const wikifolioId = data.wikifolio.id;
    const isHidden = data.certificates.some(certificate => certificate.isHidden);

    return (
        <>
            <PageEffectsHandler wikifolioId={wikifolioId} />
            {!isHidden && (
                <>
                    <WikifolioDetailBreadcrumbSchema
                        canonicalUrl={head.canonicalUrl}
                        dict={dict.breadcrumb}
                        wikifolioName={data.wikifolio.shortDescription}
                    />
                    <WikifolioDetailProductSchema canonicalUrl={head.canonicalUrl} data={data} />
                </>
            )}
            <WikifolioDetailPage wikifolioId={wikifolioId} isLoggedIn={isLoggedIn} data={data} dict={dict} head={head} />
        </>
    );
}

type IParam = IBaseParam & {
    symbol: string;
};

export const getServerSideProps: GetServerSideProps<IWikifolioDetailProps, IParam> = async context => {
    const { language, symbol } = context.params!;
    const adId = context.query?.adId as string;
    const { normalizedSymbol, isInvalidSymbol } = normalizeWikifolioSymbol(symbol);

    if (isInvalidSymbol) {
        return { notFound: true };
    }

    const [props, translations] = await addServerTiming(context.res, "ssprops", async () => {
        const props = await fetchWikifolioDetail(normalizedSymbol, context, adId);
        const translations = await serverSideTranslations(language, ["common", "feed", "wf-detail", "date-range", "reactions", "comment"]);

        return [props, translations];
    });

    if (!props) {
        return { notFound: true };
    }

    return {
        props: {
            ...props,
            head: {
                ...props.head,
                chartImageUrl: props.data?.wikifolio?.chartImageUrl || "",
                shortDescription: props.global?.gtmData?.wikifolio?.wikifolioShortDescription || "",
            },
            ...translations,
        },
    };
};

export default Page;
